import { ReactNode, useEffect, useState } from 'react';
import { logError } from 'utils/logger';
import { useQuery } from '@apollo/client';
import { useModal } from 'hooks/useModal';
import { format, subDays } from 'date-fns';
import AddIcon from '@mui/icons-material/Add';
import { ModalsKeys, Nullable } from 'global';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { IntegrationSettings } from '@trnsact/trnsact-shared-types';
import { GET_PARTNER_LINKS } from 'modules/aftermarketPartner/api';
import { Button, CircularProgress, Typography, Stack } from '@mui/material';
import { PartnerLinksResponse } from 'modules/aftermarketPartner/api/types';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { DealerManagementPartnerLink } from 'modules/aftermarketPartner/types';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { aftermarketPartnerActions, aftermarketPartnerSelectors } from 'modules/aftermarketPartner/model';
import { DealerManagementTable } from '../../tables';
import { QUERY_INTEGRATION_SETTINGS_ACCOUNT_IS_ADMIN_OF } from '../../../../../queries/common';

export const DealerManagementCard = () => {
  const history = useHistory();

  const dispatch = useDispatch();

  const account = useSelector((state: any) => state.account);
  const [offset, setOffset] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const partnerLinks = useSelector(aftermarketPartnerSelectors.partnerLinks);

  const { handleOpen: handleAddDealerDialogOpen } = useModal(ModalsKeys.AftermarketPartnerAddDealerDialog);
  const { handleOpen: openIntegrationSettingsDialog } = useModal(ModalsKeys.IntegrationSettingsDialog);

  const fetchLazyMode = (data: any) => {
    setInitialLoading(false);
    const moreDataAvailable = data.getPartnerLinks.length === 25;
    dispatch(
      aftermarketPartnerActions.setPartnerLinks(
        [...partnerLinks, ...data.getPartnerLinks].toSorted((a, b) =>
          a?.accountName?.toLowerCase()?.localeCompare(b?.accountName?.toLowerCase())
        )
      )
    );

    if (moreDataAvailable) {
      const newOffset = offset + 25;
      setOffset(newOffset);
    }
  };

  const { loading: loadingPartnerLinks, refetch: refetchPartnerLinks } = useQuery<PartnerLinksResponse>(
    GET_PARTNER_LINKS,
    {
      variables: {
        dynamicsId: account.dynamicsAccountId,
        metricsDateRange: {
          from: format(subDays(new Date(), 90), 'yyyy-MM-dd'),
          to: format(new Date(), 'yyyy-MM-dd'),
        },
        limit: 25,
        offset,
      },
      onCompleted(data) {
        fetchLazyMode(data);
      },
    }
  );

  const { data: integrationSettingsAccountIsAdminOf, loading: loadingIntegrationSettingsAccountIsAdminOf } = useQuery<{
    integrationSettingsAccountIsAdminOf: IntegrationSettings[];
  }>(QUERY_INTEGRATION_SETTINGS_ACCOUNT_IS_ADMIN_OF, {
    variables: {
      accountId: account.id,
    },
    onCompleted(data) {
      dispatch(
        aftermarketPartnerActions.setIsIntegrationAdminAccount(data.integrationSettingsAccountIsAdminOf.length > 0)
      );
    },
  });

  const handleOpenIntegrationSettingsDialog = ({ partnerLink }: { partnerLink: DealerManagementPartnerLink }) => {
    openIntegrationSettingsDialog({
      integrationName: integrationSettingsAccountIsAdminOf!.integrationSettingsAccountIsAdminOf[0].integrationName,
      dealerAccountId: partnerLink.accountId,
      dealerAccountName: partnerLink.accountName,
      dealerLocations: partnerLink.partnerLocations,
      dealerContacts: [],
    });
  };

  const onRedirectToDealerDetailsPage = (partnerLinkId: Nullable<string> | undefined) => {
    if (partnerLinkId) {
      history.push(`/dealers/${partnerLinkId}`);
    } else {
      logError('[onRedirectToDealerDetailsPage]', 'partnerLinkId null or undefined');
    }
  };

  useEffect(
    () => () => {
      dispatch(aftermarketPartnerActions.clearState());
    },
    []
  );

  return (
    <CardContainer
      title="Dealer Management"
      actionBtn={
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleAddDealerDialogOpen({ refetchPartnerLinks })}
        >
          Add Dealer
        </Button>
      }
    >
      <Stack gap="0.5rem">
        <DealerManagementTable
          tableData={partnerLinks}
          onRowClick={onRedirectToDealerDetailsPage}
          loading={initialLoading || loadingIntegrationSettingsAccountIsAdminOf}
          handleOpenIntegrationSettingsDialog={handleOpenIntegrationSettingsDialog}
        />

        <RenderWithCondition condition={loadingPartnerLinks}>
          <Stack direction="row" alignItems="center" gap="0.5rem">
            <CircularProgress size={20} />
            <Typography>Loading Pages ...</Typography>
          </Stack>
        </RenderWithCondition>
      </Stack>
    </CardContainer>
  );
};
